import React from 'react';
import { Outlet } from 'react-router-dom';
import logoWhite from '../assets/logo-white.png';
import logoDark from '../assets/logo-dark.png';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { useAnalysisSidebar } from '../contexts/AnalysisSidebarContext';
import { useAnalysisActions } from '../contexts/AnalysisActionsContext';
import { APP_VERSION } from '../version';
import './Layout.css';
import './AnalysisLayout.css';

// Chrome variant for the standalone analysis tabs (Shell / Floor
// Deformation). Same topbar + sidebar layout as the main app so the
// user feels at home, but with three specific deviations:
//   • The logo / title block is purely decorative (no navigation).
//   • Every breadcrumb crumb renders as a non-clickable label, even
//     if it carries a path.
//   • The right-hand "Go to Portal Selector" button is replaced by a
//     primary "Save and close" button that closes the analysis tab.
//   • The sidebar has no navigation items — analysis tabs are
//     intentionally a focused workspace.
export default function AnalysisLayout() {
  const { theme } = useTheme();
  const { crumbs } = useBreadcrumb();
  const { sections: sidebarSections } = useAnalysisSidebar();
  const { triggerSave, saving, hasSaveHandler } = useAnalysisActions();
  const logo = theme === 'dark' ? logoWhite : logoDark;

  const handleSaveAndClose = async () => {
    // If the current page has registered a save handler (e.g. the
    // defo pages once they have results), run it first so the
    // payload + images land on the server BEFORE we close the tab.
    // Closing without saving was the previous behaviour — kept for
    // tabs that never registered a handler (e.g. mid-run, no result
    // yet, or future tabs that don't persist anything).
    if (hasSaveHandler) {
      try {
        await triggerSave();
      } catch (e) {
        // Surface a hard error and DO NOT close — the user can fix
        // the underlying problem (e.g. server unreachable) and try
        // again rather than losing their run.
        // eslint-disable-next-line no-alert
        alert('Save failed: ' + (e?.message || e));
        return;
      }
    }
    // Tell any open inspection tab to refetch its saved defo results, so the
    // DefoSubcards there update without a manual refresh (focus alone isn't
    // reliable when this tab closes).
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        const ch = new BroadcastChannel('tib-defo-results');
        ch.postMessage({ saved: true });
        ch.close();
      }
    } catch { /* best-effort */ }
    window.close();
  };

  return (
    <div className="layout">
      {/* ── Top Bar ── */}
      <header className="topbar">
        <div className="topbar-left analysis-topbar-left" aria-disabled="true">
          <img src={logo} alt="Terra Inspectioneering" className="topbar-logo" />
          <div className="topbar-title">
            <span className="topbar-title-line1">Terra Inspectioneering</span>
            <span className="topbar-title-line2">
              Reporting Portal <span className="topbar-version">(v{APP_VERSION})</span>
            </span>
          </div>
        </div>

        {/* Breadcrumb crumbs always render as plain labels — no
            anchor / button — so they show position without offering
            navigation away from the analysis tab. */}
        {crumbs.length > 0 && (
          <div className="topbar-breadcrumb">
            {crumbs.map((crumb, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <svg
                    className="topbar-breadcrumb-chevron"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                )}
                <span
                  className={`topbar-breadcrumb-current${crumb.bold ? ' topbar-breadcrumb-bold' : ''}`}
                >
                  {crumb.label}
                </span>
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="topbar-right">
          <ThemeToggle />
          <button
            className="topbar-portal-btn analysis-save-btn"
            onClick={handleSaveAndClose}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save and close'}
          </button>
        </div>
      </header>

      {/* ── Body: page-driven sidebar + content ── */}
      <div className="layout-body">
        {sidebarSections.length > 0 && (
        <nav className="sidebar analysis-sidebar">
          {sidebarSections.map((section, sIdx) => (
            <div key={sIdx} className="a-sidebar-section">
              <h3 className="a-sidebar-heading">{section.heading}</h3>
              {(section.items || []).map((item) => {
                const isActive   = item.key === section.activeKey;
                const isPlaceholder = !!item.placeholder;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={`a-sidebar-item${isActive ? ' active' : ''}${isPlaceholder ? ' placeholder' : ''}`}
                    onClick={() => section.onSelect && section.onSelect(item.key)}
                  >
                    {item.label}
                    {isPlaceholder && (
                      <span className="a-sidebar-item-tag">soon</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        )}

        <main className="layout-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
